import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { 
  Search, 
  BookOpen, 
  HelpCircle, 
  MessageSquare, 
  FileText, 
  Zap, 
  ShieldCheck, 
  Users, 
  Settings, 
  ArrowRight, 
  ChevronRight, 
  PlayCircle,
  LifeBuoy,
  Mail,
  Smartphone,
  Layers,
  Sparkles,
  Database,
  Rocket
} from "lucide-react";
import "../assets/landing.css";
import MarketingHeader from "../components/MarketingHeader";
import MarketingFooter from "../components/MarketingFooter";
import { marketingNavLinks } from "../constants/marketingNav";

const helpCategories = [
  {
    title: "Getting Started",
    desc: "Learn the basics of PMS and set up your first project in minutes.",
    icon: Rocket,
    count: 12,
    color: "bg-blue-50",
    iconColor: "text-blue-600"
  },
  {
    title: "Account & Security",
    desc: "Manage your profile, team permissions, and credentials vault.",
    icon: ShieldCheck,
    count: 8,
    color: "bg-green-50",
    iconColor: "text-green-600"
  },
  {
    title: "Boards & Tasks",
    desc: "Deep dive into Kanban interactions, task extractions, and labels.",
    icon: Layers,
    count: 15,
    color: "bg-purple-50",
    iconColor: "text-purple-600"
  },
  {
    title: "Integrations",
    desc: "Connect Slack, GitHub, Salesforce, and other third-party tools.",
    icon: Zap,
    count: 10,
    color: "bg-orange-50",
    iconColor: "text-orange-600"
  },
  {
    title: "Billing & Plans",
    desc: "Understanding our flat-fee pricing and managing your subscription.",
    icon: FileText,
    count: 5,
    color: "bg-yellow-50",
    iconColor: "text-yellow-600"
  },
  {
    title: "Advanced Features",
    desc: "Master Gemini AI automations, custom workflows, and API usage.",
    icon: Sparkles,
    count: 9,
    color: "bg-sky",
    iconColor: "text-navy"
  }
];

const popularArticles = [
  "How to use Gemini AI for ticket extraction",
  "Inviting team members and setting roles",
  "Securing API keys in the Credentials Vault",
  "Configuring GitHub webhooks for auto-updates",
  "Migrating projects from Trello",
  "Using project categories for organization"
];

const HelpCenterPage = () => {
  const [searchQuery, setSearchQuery] = useState("");

  const navLinks = useMemo(
    () =>
      marketingNavLinks.map((link) => {
        if (link.label === "TeamBoard") return { ...link, route: "/" };
        if (link.label === "Features") return { ...link, route: "/features" };
        if (link.label === "Why TeamBoard") return { ...link, route: "/why-teamboard" };
        if (link.label === "Integrations") return { ...link, route: "/integrations" };
        if (link.label === "Pricing") return { ...link, route: "/pricing" };
        if (link.label === "Changelog") return { ...link, route: "/changelog" };
        if (link.label === "Security") return { ...link, route: "/security" };
        if (link.label === "Blog") return { ...link, route: "/blog" };
        if (link.label === "Help Center") return { ...link, route: "/help" };
        if (link.label === "About Us") return { ...link, route: "/about-us" };
        if (link.label === "Careers") return { ...link, route: "/careers" };
        if (link.label === "FAQ") return { ...link, route: "/faq" };
        if (link.label === "Contact Us") return { ...link, route: "/contact" };
        return link;
      }),
    []
  );

  const smoothScroll = useCallback((id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const headerOffset = 96;
    const offset = el.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    window.scrollTo({ top: offset, behavior: "smooth" });
  }, []);

  const handleNavClick = useCallback(
    (e, target) => {
      e.preventDefault();
      smoothScroll(target);
    },
    [smoothScroll]
  );

  useEffect(() => {
    const revealElements = document.querySelectorAll(
      ".landing-page .section-animate, .landing-page .reveal, .landing-page .reveal-left, .landing-page .reveal-right"
    );
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("active");
          }
        });
      },
      { threshold: 0.1 }
    );
    revealElements.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="landing-page font-sans antialiased text-gray-900 bg-white text-left">
      <MarketingHeader navLinks={navLinks} onNavClick={handleNavClick} ctaTarget="contact" />

      <main>
        {/* Hero Section */}
        <section id="hero" className="curved-section shell-sky section-animate relative min-h-[60vh] flex items-center overflow-hidden pt-20">
          <div className="absolute inset-0 opacity-40">
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-blue-400 rounded-full blur-[120px] -mr-40 -mt-40" />
            <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-blue-500 rounded-full blur-[100px] -ml-20 -mb-20" />
          </div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
            <div className="reveal">
              <span className="section-eyebrow">Support & Documentation</span>
              <h1 className="text-navy mt-6 mb-8">
                How can we <span className="text-blue-600">help?</span>
              </h1>
              
              <div className="max-w-2xl mx-auto relative group">
                <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-6 h-6 text-gray-400 group-focus-within:text-blue-600 transition-colors" />
                <input
                  type="text"
                  placeholder="Search for articles, guides, keywords..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-16 pr-6 py-5 bg-white border border-gray-100 rounded-[32px] shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/10 transition-all text-lg"
                />
              </div>
              
              <div className="mt-8 flex flex-wrap justify-center gap-4 text-sm font-bold text-gray-500">
                <span>Popular:</span>
                {["AI Extraction", "Salesforce Sync", "Billing", "Vault Security"].map(tag => (
                  <button key={tag} className="text-blue-600 hover:underline">{tag}</button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Categories Grid */}
        <section className="shell-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {helpCategories.map((cat, idx) => (
                <div key={idx} className="group p-10 bg-white border border-gray-100 rounded-[40px] hover:border-blue-200 hover:shadow-2xl transition-all reveal stagger-1 cursor-pointer">
                  <div className={`w-16 h-16 ${cat.color} rounded-2xl flex items-center justify-center mb-8 group-hover:scale-110 transition-transform`}>
                    <cat.icon className={`w-8 h-8 ${cat.iconColor}`} />
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{cat.title}</h3>
                  <p className="text-gray-600 mb-8 leading-relaxed text-sm">{cat.desc}</p>
                  <div className="flex items-center justify-between mt-auto">
                    <span className="text-xs font-black text-gray-400 uppercase tracking-widest">{cat.count} Articles</span>
                    <ArrowRight className="w-5 h-5 text-blue-600" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Popular Articles & Video Section */}
        <section className="shell-sky py-24">
          <div className="max-w-[95rem] mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-20 items-start">
              <div className="reveal-left">
                <h2 className="text-navy mb-12">Popular <span className="text-blue-600">Articles.</span></h2>
                <div className="space-y-4">
                  {popularArticles.map((article, i) => (
                    <div key={i} className="flex items-center justify-between p-6 bg-white rounded-2xl border border-white hover:border-blue-200 transition-all cursor-pointer group shadow-sm">
                      <span className="text-lg font-bold text-gray-700 group-hover:text-blue-600 transition-colors">{article}</span>
                      <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-blue-600" />
                    </div>
                  ))}
                </div>
              </div>
              
              <div className="reveal-right">
                <div className="bg-navy rounded-[40px] p-12 text-white shadow-2xl relative overflow-hidden">
                  <div className="absolute top-0 right-0 p-8 opacity-10">
                    <PlayCircle className="w-32 h-32 text-white" />
                  </div>
                  <h3 className="text-3xl font-bold mb-6">Video Tutorials</h3>
                  <p className="text-blue-100 text-lg mb-10">Prefer watching to reading? Our video library covers everything from basic setup to advanced AI workflows.</p>
                  <div className="space-y-6">
                    <div className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl border border-white/10 hover:bg-white/20 transition-all cursor-pointer">
                      <div className="w-12 h-12 rounded-lg bg-sky flex items-center justify-center text-navy">
                        <PlayCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-bold">Introduction to Kanban</p>
                        <p className="text-xs text-blue-200">2:45 • Setup Guide</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 p-4 bg-white/10 rounded-2xl border border-white/10 hover:bg-white/20 transition-all cursor-pointer">
                      <div className="w-12 h-12 rounded-lg bg-sky flex items-center justify-center text-navy">
                        <PlayCircle className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-bold">Mastering the Vault</p>
                        <p className="text-xs text-blue-200">4:12 • Security Guide</p>
                      </div>
                    </div>
                  </div>
                  <button className="btn-secondary !bg-sky !text-navy !border-sky w-full mt-10">
                    Watch All Tutorials
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Contact Support Options */}
        <section className="shell-white py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="reveal mb-16">
              <span className="section-eyebrow">Still need help?</span>
              <h2 className="mt-4 mb-6">Contact our <span className="text-blue-600">Support Team.</span></h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Can't find the answer you're looking for? Our team of project management experts is here to help.
              </p>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="p-10 bg-gray-50 rounded-[40px] border border-gray-100 reveal stagger-1">
                <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-8 text-blue-600">
                  <Mail className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-bold mb-4">Email Support</h4>
                <p className="text-gray-600 text-sm mb-8">Best for detailed technical questions. Response time: &lt; 2 hours.</p>
                <Link to="/contact" className="text-blue-600 font-black flex items-center justify-center gap-2 mx-auto">
                  Send an email <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              
              <div className="p-10 bg-gray-50 rounded-[40px] border border-gray-100 reveal stagger-2">
                <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-8 text-green-600">
                  <MessageSquare className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-bold mb-4">Live Chat</h4>
                <p className="text-gray-600 text-sm mb-8">Chat with our experts in real-time. Available Mon-Fri, 9am-6pm IST.</p>
                <button className="text-green-600 font-black flex items-center justify-center gap-2 mx-auto">
                  Start a chat <ArrowRight className="w-4 h-4" />
                </button>
              </div>
              
              <div className="p-10 bg-gray-50 rounded-[40px] border border-gray-100 reveal stagger-3">
                <div className="w-16 h-16 bg-purple-50 rounded-2xl flex items-center justify-center mx-auto mb-8 text-purple-600">
                  <Smartphone className="w-8 h-8" />
                </div>
                <h4 className="text-xl font-bold mb-4">Community</h4>
                <p className="text-gray-600 text-sm mb-8">Join the PMS community on Discord to learn from other teams.</p>
                <button className="text-purple-600 font-black flex items-center justify-center gap-2 mx-auto">
                  Join Discord <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="shell-navy relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-sky rounded-full blur-[120px]" />
          </div>
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-white mb-8">Ready to transform your productivity?</h2>
            <p className="text-xl text-blue-100 mb-12">
              Start building better projects today with PMS intelligent workspace.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact" className="btn-secondary">
                Get Started Free
              </Link>
              <Link to="/" className="btn-secondary !bg-transparent !text-white !border-white/20 hover:!bg-white/5">
                Back to Home
              </Link>
            </div>
          </div>
        </section>
      </main>

      <MarketingFooter onNavClick={handleNavClick} />
    </div>
  );
};

export default HelpCenterPage;
